import React, { FC, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isEqual } from "lodash-es";
import { useIntl } from "react-intl";

import Visibility from "@mapmycustomers/shared/enum/Visibility";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, SelectField } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import SettingPage from "../common/SettingPage";

import TagsList from "./components/TagsList";
import styles from "./FileTags.module.scss";
import messages from "./messages";
import { getFileTags } from "./store";
import { bulkCreateFileTags, bulkEditFileTags, initialize } from "./store/actions";

type FileTagsProps = {} & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>;

const FileTags: FC<FileTagsProps> = ({
  onBulkCreateFileTags,
  onBulkEditFileTags,
  onInitialize,
  tags,
}) => {
  const intl = useIntl();

  const [hasChanges, setChanges, unsetChanges] = useBoolean();
  const [addingTags, startAdding, stopAdding] = useBoolean();

  const [fileTags, setFileTags] = useState(tags);
  const [newTagNames, setNewTagNames] = useState<FileTag["name"][]>([]);

  const handleCreateTags = useCallback(() => {
    onBulkCreateFileTags({
      callback: () => {
        stopAdding();
        setNewTagNames([]);
      },
      fileTags: newTagNames.map((name) => ({ name, visibility: Visibility.PRIVATE })),
    });
  }, [newTagNames, onBulkCreateFileTags, stopAdding]);

  const handleCancelCreateTags = useCallback(() => {
    stopAdding();
    setNewTagNames([]);
  }, [stopAdding]);

  const handleChange = useCallback(
    (tags: FileTag[]) => {
      setChanges();
      setFileTags(tags);
    },
    [setChanges]
  );

  const handleUndo = useCallback(() => {
    unsetChanges();
    setFileTags(tags);
  }, [tags, unsetChanges]);

  const handleSaveChanges = useCallback(() => {
    const updatedTags = fileTags.filter(
      (updatedTag) =>
        !isEqual(
          tags.find(({ id }) => id == updatedTag.id),
          updatedTag
        )
    );

    if (updatedTags.length) {
      onBulkEditFileTags({
        callback: () => {
          unsetChanges();
        },
        fileTags: updatedTags,
      });
    } else {
      unsetChanges();
    }
  }, [fileTags, onBulkEditFileTags, tags, unsetChanges]);

  useEffect(() => {
    unsetChanges();
    setFileTags(tags);
  }, [tags, unsetChanges]);

  useEffect(() => {
    onInitialize();
  }, [onInitialize]);

  return (
    <SettingPage
      changesSaving={false}
      hasChanges={hasChanges}
      onSaveChanges={handleSaveChanges}
      onUnDo={handleUndo}
      title={intl.formatMessage(messages.title)}
    >
      <div className={styles.container}>
        <div className={styles.description}>
          <span>{intl.formatMessage(messages.description)}</span>
          <Button leftIcon={<FontAwesomeIcon icon={faPlus} />} onClick={startAdding} type="primary">
            {intl.formatMessage(messages.addTag)}
          </Button>
        </div>

        {addingTags && (
          <>
            <div>
              <SelectField<FileTag["name"][]>
                className={styles.addTagsSelect}
                label={intl.formatMessage(messages.addTags)}
                mode="tags"
                onChange={setNewTagNames}
                placeholder={intl.formatMessage(messages.placeholder)}
                popupClassName={styles.selectPopup}
                size="middle"
                tokenSeparators={[","]}
                value={newTagNames}
              />
            </div>
            <div className={styles.addTagsButtons}>
              <Button onClick={handleCancelCreateTags}>
                {intl.formatMessage(messages.cancel)}
              </Button>
              <Button disabled={!newTagNames.length} onClick={handleCreateTags} type="primary">
                {intl.formatMessage(messages.save)}
              </Button>
            </div>
          </>
        )}
        <TagsList fileTags={fileTags} onChange={handleChange} />
      </div>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  tags: getFileTags(state),
});

const mapDispatchToProps = {
  onBulkCreateFileTags: bulkCreateFileTags.request,
  onBulkEditFileTags: bulkEditFileTags.request,
  onInitialize: initialize.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileTags);
